import { useCallback, useEffect, useRef, useState } from 'react';
import { errorCodeFromCloseCode, errorCodeFromMessage, getErrorMeta } from '../errors';
import { maybeJsonRecord } from '../runtime-validation';
import { createAcpWebSocketTransport } from '../transport/websocket';
import { addJitter, classifyCloseCode, DEFAULT_RECONNECT_DELAY_MS, DEFAULT_RECONNECT_MAX_DELAY_MS, DEFAULT_RECONNECT_TIMEOUT_MS, mapAgentStatusToSessionState, safeHost, } from './useAcpSession.helpers';
export { addJitter, classifyCloseCode };
function isGatewayErrorMessage(data) {
    if (!data || typeof data !== 'object') {
        return false;
    }
    const record = maybeJsonRecord(data);
    if (!record)
        return false;
    return typeof record.error === 'string' &&
        (typeof record.message === 'string' || record.message === undefined);
}
/**
 * React hook for managing an ACP session with the VM Agent gateway.
 *
 * Handles:
 * - WebSocket connection to /agent/ws
 * - Agent selection via select_agent control messages
 * - Agent status tracking (starting -> ready -> prompting -> etc.)
 * - Reconnection with exponential backoff on unexpected disconnect
 */
export function useAcpSession(options) {
    const { wsUrl, resolveWsUrl, onAcpMessage, onLifecycleEvent, onPrepareForReplay, onFirstConnect, reconnectDelayMs = DEFAULT_RECONNECT_DELAY_MS, reconnectTimeoutMs = DEFAULT_RECONNECT_TIMEOUT_MS, reconnectMaxDelayMs = DEFAULT_RECONNECT_MAX_DELAY_MS, } = options;
    const [state, setState] = useState('disconnected');
    const [agentType, setAgentType] = useState(null);
    const [error, setError] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const [replaying, setReplaying] = useState(false);
    /** Set both error message and structured code together. Uses error metadata for the message when not provided. */
    const setStructuredError = useCallback((code, message) => {
        const meta = getErrorMeta(code);
        setErrorCode(code);
        setError(message ?? meta.userMessage);
    }, []);
    /** Clear both error and errorCode */
    const clearError = useCallback(() => {
        setError(null);
        setErrorCode(null);
    }, []);
    const transportRef = useRef(null);
    const onAcpMessageRef = useRef(onAcpMessage);
    onAcpMessageRef.current = onAcpMessage;
    const onLifecycleEventRef = useRef(onLifecycleEvent);
    onLifecycleEventRef.current = onLifecycleEvent;
    const onPrepareForReplayRef = useRef(onPrepareForReplay);
    onPrepareForReplayRef.current = onPrepareForReplay;
    const onFirstConnectRef = useRef(onFirstConnect);
    onFirstConnectRef.current = onFirstConnect;
    const wsUrlRef = useRef(wsUrl);
    wsUrlRef.current = wsUrl;
    const resolveWsUrlRef = useRef(resolveWsUrl);
    resolveWsUrlRef.current = resolveWsUrl;
    // Track whether we've called onFirstConnect for this connection
    const hasCalledFirstConnectRef = useRef(false);
    // Track the server-reported status so we can restore it after replay completes.
    // Without this, reconnecting during a prompt transitions replaying → ready,
    // even though the server is still in 'prompting' (deadlocking new prompts).
    const serverStatusRef = useRef('');
    // Track prompt completion observed during replay so replay_complete does not
    // restore a stale prompting snapshot captured before replay started.
    const replaySawPromptDoneRef = useRef(false);
    // Guard against re-entering replay mode after a replay has just completed.
    // Set to true in handleSessionReplayComplete, cleared on new WebSocket connection.
    // Prevents a post-replay session_state with stale replayCount > 0 from
    // triggering prepareForReplay() and wiping all just-replayed messages.
    const replayCompletedRef = useRef(false);
    // Track most recent URL used for connection attempts.
    const connectUrlRef = useRef(wsUrl);
    // When true, the next session_state with status=error should be treated as
    // no_session so the auto-select logic in ChatSession triggers agent restart.
    // Set in reconnect(), consumed in handleSessionState.
    const pendingAgentRestartRef = useRef(false);
    // Reconnection state (refs to avoid re-triggering the effect)
    const reconnectTimerRef = useRef(null);
    const reconnectStartRef = useRef(0);
    const reconnectAttemptRef = useRef(0);
    const intentionalCloseRef = useRef(false);
    const wasConnectedRef = useRef(false);
    const attemptReconnectRef = useRef(() => { });
    // Last close-code-derived error code — used to enrich RECONNECT_TIMEOUT
    const lastCloseErrorCodeRef = useRef(null);
    // Lifecycle logging helper
    const logLifecycle = useCallback((level, message, context) => {
        onLifecycleEventRef.current?.({ source: 'acp-session', level, message, context });
    }, []);
    // Map VM Agent status to session state
    const handleAgentStatus = useCallback((msg) => {
        const newState = mapAgentStatusToSessionState(msg.status);
        setState(newState);
        setAgentType(msg.agentType);
        logLifecycle('info', `Agent status: ${msg.status}`, {
            agentType: msg.agentType,
            status: msg.status,
            mappedState: newState,
            ...(msg.error ? { error: msg.error } : {}),
        });
        if (msg.error) {
            const code = errorCodeFromMessage(msg.error);
            setStructuredError(code, msg.error);
        }
        else if (newState !== 'error') {
            clearError();
        }
    }, [logLifecycle, setStructuredError, clearError]);
    // Handle incoming ACP messages
    const handleAcpMessage = useCallback((data) => {
        if (isGatewayErrorMessage(data)) {
            logLifecycle('error', 'Gateway error received', {
                error: data.error,
                message: data.message,
            });
            setState('error');
            const errMsg = data.message || data.error;
            setStructuredError(errorCodeFromMessage(errMsg), errMsg);
            return;
        }
        onAcpMessageRef.current?.(data);
    }, [logLifecycle, setStructuredError]);
    // Handle session_state control message from SessionHost on viewer attach.
    // This tells us the current server-side state of the agent session.
    const handleSessionState = useCallback((msg) => {
        logLifecycle('info', 'Session state received', {
            status: msg.status,
            agentType: msg.agentType,
            replayCount: msg.replayCount,
            error: msg.error,
        });
        // Map SessionHost status to our state machine
        const status = msg.status;
        // Remember the server-reported status so handleSessionReplayComplete
        // can restore it (e.g., 'prompting') instead of defaulting to 'ready'.
        serverStatusRef.current = status;
        // Always sync agentType from server state — clear to null when empty/idle
        setAgentType(msg.agentType || null);
        if (msg.error) {
            setStructuredError(errorCodeFromMessage(msg.error), msg.error);
        }
        else {
            clearError();
        }
        // Clear the pending-restart flag for non-error states — the agent is
        // already running (or idle), so no restart is needed.
        if (status !== 'error') {
            pendingAgentRestartRef.current = false;
        }
        // Call onFirstConnect exactly once when we receive the first session_state
        // after a successful connection. This replaces the useEffect-based auto-select
        // logic and prevents infinite loops by design.
        if (!hasCalledFirstConnectRef.current && onFirstConnectRef.current) {
            hasCalledFirstConnectRef.current = true;
            onFirstConnectRef.current(msg);
        }
        if (status === 'idle') {
            // No agent selected yet — equivalent to no_session
            setState('no_session');
            setReplaying(false);
        }
        else if (status === 'starting') {
            setState('initializing');
            setReplaying(false);
        }
        else if (status === 'ready' || status === 'prompting') {
            // Agent is running — we'll receive buffered messages, then replay_complete
            if (msg.replayCount > 0 && !replayCompletedRef.current) {
                // Clear conversation items SYNCHRONOUSLY before replay messages arrive.
                // This avoids the race where useEffect-based clear runs after replay
                // messages have already been appended (causing jumbled/duplicate text).
                // The replayCompletedRef guard prevents the post-replay authoritative
                // session_state snapshot (which has stale replayCount > 0) from
                // triggering a second clear that would wipe all just-replayed messages.
                onPrepareForReplayRef.current?.();
                replaySawPromptDoneRef.current = false;
                setState('replaying');
                setReplaying(true);
            }
            else {
                replaySawPromptDoneRef.current = false;
                setState(status === 'prompting' ? 'prompting' : 'ready');
                setReplaying(false);
            }
        }
        else if (status === 'error') {
            if (pendingAgentRestartRef.current) {
                // Manual reconnect landed on an errored session — treat as no_session
                // so the auto-select logic in ChatSession re-selects the agent, which
                // triggers SelectAgent on the SessionHost and restarts the process.
                pendingAgentRestartRef.current = false;
                logLifecycle('info', 'Pending agent restart: treating error as no_session for re-selection', {
                    agentType: msg.agentType,
                    error: msg.error,
                });
                setAgentType(null);
                clearError();
                setState('no_session');
            }
            else {
                setState('error');
            }
            setReplaying(false);
        }
        else if (status === 'stopped') {
            setState('disconnected');
            setReplaying(false);
        }
        else {
            // Unknown status — treat as no_session
            setState('no_session');
            setReplaying(false);
        }
    }, [logLifecycle, setStructuredError, clearError]);
    // Handle session_replay_complete — all buffered messages have been delivered
    const handleSessionReplayComplete = useCallback(() => {
        const serverStatus = serverStatusRef.current;
        const sawPromptDone = replaySawPromptDoneRef.current;
        replaySawPromptDoneRef.current = false;
        // Mark replay as completed so subsequent session_state messages with stale
        // replayCount > 0 (e.g., the post-replay authoritative snapshot) do not
        // trigger another prepareForReplay cycle that would wipe all messages.
        replayCompletedRef.current = true;
        logLifecycle('info', 'Session replay complete', { serverStatus, sawPromptDone });
        setReplaying(false);
        // Restore the server-reported status instead of unconditionally going to
        // 'ready'. If the agent was 'prompting' when we reconnected, we must stay
        // in 'prompting' so the input is disabled and the cancel button is shown.
        // Without this, the user can submit a new prompt that deadlocks on the
        // server's promptMu (the previous prompt is still blocking).
        setState((prev) => {
            if (prev !== 'replaying')
                return prev;
            if (sawPromptDone)
                return 'ready';
            if (serverStatus === 'prompting')
                return 'prompting';
            return 'ready';
        });
    }, [logLifecycle]);
    // Handle session prompting state changes
    const handleSessionPrompting = useCallback((prompting) => {
        logLifecycle('info', `Session prompting: ${prompting}`);
        if (prompting) {
            setState('prompting');
        }
        else {
            // Mark prompt completion immediately so replay_complete in the same
            // event loop tick can observe it before React flushes state updates.
            replaySawPromptDoneRef.current = true;
            setState((prev) => {
                return prev === 'prompting' ? 'ready' : prev;
            });
        }
    }, [logLifecycle]);
    // Connect to the ACP WebSocket
    const connect = useCallback((url) => {
        // Close any stale transport before opening a new connection.
        // This prevents duplicate connections when a reconnect fires while a
        // previous WebSocket is still in CLOSING state.
        if (transportRef.current) {
            intentionalCloseRef.current = true;
            transportRef.current.close();
            transportRef.current = null;
            intentionalCloseRef.current = false;
        }
        const host = safeHost(url);
        const ws = new WebSocket(url);
        ws.addEventListener('open', () => {
            // Reset reconnection state on successful connect
            const wasReconnect = wasConnectedRef.current;
            reconnectAttemptRef.current = 0;
            reconnectStartRef.current = 0;
            wasConnectedRef.current = true;
            // Reset the replay-completed guard so the new connection's first
            // session_state with replayCount > 0 correctly enters replay mode.
            replayCompletedRef.current = false;
            // Reset the first-connect flag so onFirstConnect can fire again
            hasCalledFirstConnectRef.current = false;
            // Stay in 'connecting' until we receive session_state from the server.
            // The server will send session_state immediately after the viewer
            // attaches, telling us whether an agent is already running.
            setState('connecting');
            clearError();
            logLifecycle('info', 'WebSocket connected, awaiting session_state', { host, wasReconnect });
        });
        const transport = createAcpWebSocketTransport({
            ws,
            onAgentStatus: handleAgentStatus,
            onAcpMessage: handleAcpMessage,
            onAgentCrashReport: handleAcpMessage,
            onSessionState: handleSessionState,
            onSessionReplayComplete: handleSessionReplayComplete,
            onSessionPrompting: handleSessionPrompting,
            onClose(code, reason) {
                // WebSocket closed — attempt reconnection if not intentional
                transportRef.current = null;
                const strategy = classifyCloseCode(code);
                logLifecycle('info', 'WebSocket closed', {
                    host,
                    code,
                    reason,
                    strategy,
                    intentional: intentionalCloseRef.current,
                    wasConnected: wasConnectedRef.current,
                });
                if (intentionalCloseRef.current) {
                    setState('disconnected');
                    return;
                }
                // Server explicitly rejected us (auth failure, policy) — don't reconnect
                if (strategy === 'no-reconnect') {
                    const errCode = errorCodeFromCloseCode(code);
                    logLifecycle('warn', 'Server closed connection cleanly — not reconnecting', { code, reason, errorCode: errCode });
                    setState('error');
                    setStructuredError(errCode);
                    return;
                }
                // Only reconnect if we were previously connected
                if (wasConnectedRef.current) {
                    // Stash the close-code-derived error code so the UI can show it
                    // during reconnection if it eventually times out.
                    lastCloseErrorCodeRef.current = errorCodeFromCloseCode(code);
                    attemptReconnectRef.current();
                }
                else {
                    logLifecycle('error', 'WebSocket connection failed (never connected)', { host, code, reason });
                    setState('error');
                    setStructuredError('CONNECTION_FAILED');
                }
            },
            onError() {
                // WebSocket error
                logLifecycle('warn', 'WebSocket error event', {
                    host,
                    wasConnected: wasConnectedRef.current,
                    intentionalClose: intentionalCloseRef.current,
                });
                if (!intentionalCloseRef.current && wasConnectedRef.current) {
                    // Will be followed by close event which handles reconnection
                    return;
                }
                setState('error');
                setStructuredError('CONNECTION_FAILED');
            },
            onLifecycleEvent: onLifecycleEventRef.current,
        });
        transportRef.current = transport;
        return transport;
    }, [handleAgentStatus, handleAcpMessage, handleSessionState, handleSessionReplayComplete, handleSessionPrompting, logLifecycle, clearError, setStructuredError]); // eslint-disable-line react-hooks/exhaustive-deps
    const resolveConnectUrl = useCallback((fallbackUrl) => {
        if (resolveWsUrlRef.current) {
            return resolveWsUrlRef.current();
        }
        return fallbackUrl ?? wsUrlRef.current;
    }, []);
    const connectWithResolvedUrl = useCallback((fallbackUrl) => {
        const handleResolved = (resolved) => {
            if (!resolved) {
                setState('error');
                setStructuredError('URL_UNAVAILABLE');
                return false;
            }
            connectUrlRef.current = resolved;
            connect(resolved);
            return true;
        };
        const handleError = (err) => {
            const message = err instanceof Error ? err.message : 'Failed to resolve WebSocket URL';
            logLifecycle('warn', 'Failed to resolve WebSocket URL', { error: message });
            setState('error');
            setStructuredError('URL_UNAVAILABLE', message);
            return false;
        };
        try {
            const resolvedOrPromise = resolveConnectUrl(fallbackUrl);
            if (resolvedOrPromise &&
                typeof resolvedOrPromise === 'object' &&
                'then' in resolvedOrPromise) {
                return resolvedOrPromise
                    .then((resolved) => handleResolved(resolved))
                    .catch((err) => handleError(err));
            }
            return Promise.resolve(handleResolved(resolvedOrPromise));
        }
        catch (err) {
            return Promise.resolve(handleError(err));
        }
    }, [connect, logLifecycle, resolveConnectUrl, setStructuredError]);
    // Attempt reconnection with exponential backoff
    const attemptReconnect = useCallback(() => {
        // Check if browser is offline — pause reconnection until online
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            logLifecycle('info', 'Browser is offline — waiting for network before reconnecting');
            setState('error');
            setStructuredError('NETWORK_OFFLINE');
            // The online event listener (registered below) will resume reconnection
            return;
        }
        const now = Date.now();
        // Start the reconnect timer on first attempt
        if (reconnectStartRef.current === 0) {
            reconnectStartRef.current = now;
        }
        // Check total timeout
        const elapsed = now - reconnectStartRef.current;
        if (elapsed >= reconnectTimeoutMs) {
            logLifecycle('error', 'Reconnection timed out', {
                elapsedMs: elapsed,
                timeoutMs: reconnectTimeoutMs,
                totalAttempts: reconnectAttemptRef.current,
                lastCloseErrorCode: lastCloseErrorCodeRef.current,
            });
            setState('error');
            setStructuredError('RECONNECT_TIMEOUT');
            reconnectStartRef.current = 0;
            reconnectAttemptRef.current = 0;
            lastCloseErrorCodeRef.current = null;
            return;
        }
        setState('reconnecting');
        const attempt = reconnectAttemptRef.current++;
        const baseDelay = Math.min(reconnectDelayMs * Math.pow(2, attempt), reconnectMaxDelayMs);
        // Add ±25% jitter to prevent thundering-herd when many clients reconnect
        const delay = addJitter(baseDelay);
        logLifecycle('info', `Reconnect attempt ${attempt + 1}`, {
            attempt: attempt + 1,
            baseDelayMs: baseDelay,
            jitteredDelayMs: delay,
            elapsedMs: elapsed,
            timeoutMs: reconnectTimeoutMs,
        });
        reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            void connectWithResolvedUrl(connectUrlRef.current).then((ok) => {
                if (!ok) {
                    attemptReconnectRef.current();
                }
            });
        }, delay);
    }, [reconnectDelayMs, reconnectTimeoutMs, reconnectMaxDelayMs, connectWithResolvedUrl, logLifecycle, setStructuredError]);
    attemptReconnectRef.current = attemptReconnect;
    // Main connection effect
    useEffect(() => {
        if (!wsUrl && !resolveWsUrlRef.current) {
            setState('disconnected');
            return;
        }
        intentionalCloseRef.current = false;
        wasConnectedRef.current = false;
        reconnectAttemptRef.current = 0;
        reconnectStartRef.current = 0;
        setState('connecting');
        clearError();
        logLifecycle('info', 'Initiating connection', {
            host: wsUrl ? safeHost(wsUrl) : 'resolver',
        });
        void connectWithResolvedUrl(wsUrl);
        return () => {
            logLifecycle('info', 'Connection cleanup (intentional close)');
            intentionalCloseRef.current = true;
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            if (transportRef.current) {
                transportRef.current.close();
            }
            transportRef.current = null;
        };
    }, [wsUrl, connectWithResolvedUrl, logLifecycle]);
    // Reconnect immediately when tab becomes visible again (mobile background tab fix)
    useEffect(() => {
        if (!wsUrl && !resolveWsUrlRef.current)
            return;
        if (typeof document === 'undefined')
            return;
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible')
                return;
            // Only reconnect if we were previously connected and WebSocket is no longer open
            if (!wasConnectedRef.current)
                return;
            if (transportRef.current?.connected)
                return;
            logLifecycle('info', 'Tab became visible, triggering reconnect');
            // Cancel any pending backoff timer — reconnect immediately
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            // Reset backoff state for a fresh immediate attempt
            reconnectAttemptRef.current = 0;
            reconnectStartRef.current = 0;
            intentionalCloseRef.current = false;
            setState('reconnecting');
            void connectWithResolvedUrl(connectUrlRef.current);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [wsUrl, connectWithResolvedUrl, logLifecycle]);
    // Resume reconnection when browser comes back online
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const handleOnline = () => {
            logLifecycle('info', 'Browser came online');
            // Only auto-reconnect if we were in the NETWORK_OFFLINE error state
            if (!wasConnectedRef.current)
                return;
            if (transportRef.current?.connected)
                return;
            // Reset backoff and try immediately
            reconnectAttemptRef.current = 0;
            reconnectStartRef.current = 0;
            intentionalCloseRef.current = false;
            clearError();
            setState('reconnecting');
            void connectWithResolvedUrl(connectUrlRef.current);
        };
        const handleOffline = () => {
            logLifecycle('warn', 'Browser went offline');
            // If we're currently trying to reconnect, stop wasting attempts
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            // Only set offline error if we lost an active connection
            if (wasConnectedRef.current && !transportRef.current?.connected) {
                setState('error');
                setStructuredError('NETWORK_OFFLINE');
            }
        };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [connectWithResolvedUrl, logLifecycle, clearError, setStructuredError]);
    // Manual reconnect (exposed to UI for "Reconnect" button)
    const reconnect = useCallback(() => {
        if (!wsUrl && !resolveWsUrlRef.current)
            return;
        logLifecycle('info', 'Manual reconnect triggered');
        // Close existing transport if any
        if (transportRef.current) {
            intentionalCloseRef.current = true;
            transportRef.current.close();
            transportRef.current = null;
        }
        // Cancel pending timer
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        // If we're in an error state, signal that the next session_state with
        // status=error should trigger agent re-selection instead of staying stuck.
        // This lets the "Reconnect" button actually restart a crashed agent.
        pendingAgentRestartRef.current = true;
        // Reset state and reconnect
        reconnectAttemptRef.current = 0;
        reconnectStartRef.current = 0;
        intentionalCloseRef.current = false;
        wasConnectedRef.current = true; // We want to reconnect
        clearError();
        setState('reconnecting');
        void connectWithResolvedUrl(connectUrlRef.current);
    }, [wsUrl, connectWithResolvedUrl, logLifecycle, clearError]);
    // Switch to a different agent
    const switchAgent = useCallback((newAgentType) => {
        if (transportRef.current?.connected) {
            logLifecycle('info', `Switching agent to ${newAgentType}`, { agentType: newAgentType });
            transportRef.current.sendSelectAgent(newAgentType);
            setState('initializing');
            setAgentType(newAgentType);
            clearError();
        }
    }, [logLifecycle, clearError]);
    // Send a raw ACP message
    const sendMessage = useCallback((message) => {
        if (transportRef.current?.connected) {
            transportRef.current.sendAcpMessage(message);
        }
    }, []);
    return {
        state,
        agentType,
        error,
        errorCode,
        replaying,
        switchAgent,
        sendMessage,
        connected: transportRef.current?.connected ?? false,
        reconnect,
    };
}
//# sourceMappingURL=useAcpSession.js.map